import { useTranslation } from "react-i18next";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import BackupOutlinedIcon from "@mui/icons-material/BackupOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import RestoreOutlinedIcon from "@mui/icons-material/RestoreOutlined";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AssignmentLateOutlinedIcon from "@mui/icons-material/AssignmentLateOutlined";
import ChecklistRtlOutlinedIcon from "@mui/icons-material/ChecklistRtlOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import WorkHistoryOutlinedIcon from "@mui/icons-material/WorkHistoryOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import RuleFolderOutlinedIcon from "@mui/icons-material/RuleFolderOutlined";
import { useAuth } from "src/shared/context/useAuth";
import useGetBusinessMenu from "../data-access/useGetBusinessMenu";
import OutlinedFlagIcon from "@mui/icons-material/OutlinedFlag";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";

export const useDrawerOptions = () => {
  const { t } = useTranslation();
  const {
    isBusiness,
    isChamber,
    isOrgAdmin,
    isSystemAdmin,
    isCashier,
    isNonGemiUser,
    isRegion,
    isGGEAdmin,
    isGGE,
    isKEE,
    isChamberOnly,
    chamberHasOneAdmin,
    isSampleInitiator,
    isSupervisorAssignee,
    isSupervisorAssigner,
  } = useAuth();

  const { data: businessMenuResponse } = useGetBusinessMenu({
    enabled: isBusiness,
  });

  if (isBusiness) {
    return [
      {
        label: t("Αιτήσεις μεταβολής"),
        image: (
          <ArticleOutlinedIcon
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              color: "#003476",
            }}
          />
        ),
        level: 0,
        badge: 1,
        children: [
          {
            label: t("Νέα Αίτηση"),
            level: 1,
            link: "/business/newApplicationForm",
          },
          {
            label: t("Αιτήσεις υπό διεκπεραίωση"),
            level: 1,
            link: "/business/activeApplications",
          },
          {
            label: t("Διεκπεραιωμένες Αιτήσεις"),
            level: 1,
            link: "/business/pastApplications",
          },
        ],
      },
      {
        label: t("Πιστοποιητικά / Αντίγραφα"),
        image: (
          <DescriptionOutlinedIcon
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              color: "#003476",
            }}
          />
        ),
        level: 0,
        link: "/admin/users/ff",
        children: [
          {
            label: t("Νέα Αίτηση για Πιστοποιητικά / Αντίγραφα"),
            level: 1,
            link: "/certificate/newCertificateApplicationForm",
          },
          {
            label: t("Αιτήσεις υπό διεκπεραίωση"),
            level: 1,
            link: "/certificate/activeCertificateApplications",
          },
          {
            label: t("Διεκπεραιωμένες Αιτήσεις"),
            level: 1,
            link: "/certificate/pastCertificateApplications",
          },
        ],
      },
      {
        label: t("Έγγραφα"),
        image: (
          <BackupOutlinedIcon
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              color: "#003476",
            }}
          />
        ),
        level: 0,
        children: [
          {
            label: t("Τα έγγραφά μου"),
            level: 1,

            link: "/business/files",
          },
          ...(businessMenuResponse?.data?.isListedCompany
            ? [
                {
                  label: t("Τα XBRL / XHTML έγγραφά μου"),
                  level: 1,
                  link: "/business/xbrlFiles",
                },
              ]
            : []),
        ],
      },
      {
        label: t("Ετήσιο τέλος ΓΕΜΗ"),
        image: (
          <AccountBalanceOutlinedIcon
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              color: "#003476",
            }}
          />
        ),
        level: 0,
        children: [
          {
            label: t("Πληρωμή νέου ετήσιου Τέλους Τήρησης Μερίδας στο Γ.Ε.ΜΗ."),
            level: 1,
            link: "/payment/listCompObligations",
          },
          {
            label: t("Πληρωμένα Ετήσια Τέλη Τήρησης Μερίδας στο Γ.Ε.ΜΗ."),
            level: 1,
            link: "/payment/listSubscriptions",
          },
        ],
      },
      {
        label: t("Ιστορικό Πληρωμών"),
        image: (
          <RestoreOutlinedIcon
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              color: "#003476",
            }}
          />
        ),
        level: 0,
        children: [
          {
            label: t("Μεταβολής/Ετήσιων Τελών Τήρησης Μερίδας"),
            level: 1,
            link: "/payment/listPayments",
          },
          {
            label: t("Πιστοποιητικών"),
            level: 1,
            link: "/payment/listCertificatePayments",
          },
          ...(import.meta.env.VITE_VIOLATION_CASES_ACTIVATED === "true"
            ? [
                {
                  label: t("Προστίμων"),
                  level: 1,
                  link: "/payment/listFinePayments",
                },
              ]
            : []),
        ],
      },
      ...(businessMenuResponse?.data?.approvedCompanyByLegalTypes
        ? [
            {
              label: t("Διάθεση Ηλεκτρονικής Υπογραφής"),
              image: (
                <CreateOutlinedIcon
                  sx={{
                    width: "1.5rem",
                    height: "1.5rem",
                    color: "#003476",
                  }}
                />
              ),
              level: 0,
              children: [
                {
                  label: t("Επιλογή Φυσικών Προσώπων"),
                  level: 1,
                  link: "/beneficiary/viewBeneficiaryPersons",
                },
                {
                  label: t("Ιστορικό Επιλογής Φυσικών Προσώπων"),
                  level: 1,
                  link: "/beneficiary/beneficiaryPersonHistory",
                },
                {
                  label: t("Ιστορικό Αιτημάτων Προσθήκης Εκπροσώπων"),
                  level: 1,
                  link: "/beneficiary/representativesRequestsHistory",
                },
                {
                  label: t("Οδηγίες Χρήσης"),
                  level: 1,
                  isExternal: true,
                  link: "/manuals/manualBusinessDigitalSignature.pdf",
                },
              ],
            },
          ]
        : []),
      {
        label: t("Ενημέρωση για μεταβολές επιχείρησης"),
        image: (
          <InfoOutlinedIcon
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              color: "#003476",
            }}
          />
        ),
        level: 0,
        link: "/admin/roles/dashboard",
        children: [
          {
            label: t("Θέλω να ακολουθήσω νέα επιχείρηση"),
            level: 1,
            link: "/followCompany/searchCompanyToFollow",
          },
          {
            label: t("Λίστα επιχειρήσεων που ακολουθώ"),
            level: 1,
            link: "/followCompany/myFollow",
          },
        ],
      },
      ...(businessMenuResponse?.data?.companySuspension
        ? [
            {
              label: t("Ανακοινώσεις Αναστολής"),
              image: (
                <AssignmentLateOutlinedIcon
                  sx={{
                    width: "1.5rem",
                    height: "1.5rem",
                    color: "#003476",
                  }}
                />
              ),
              level: 0,
              children: [
                {
                  label: t("Ιστορικό Ανακοινώσεων"),
                  level: 1,
                  link: "/companySuspension/listOfSuspensions",
                },
              ],
            },
          ]
        : []),
      {
        label: t("Προδέσμευση Επωνυμίας / ΔΤ"),
        image: (
          <ChecklistRtlOutlinedIcon
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              color: "#003476",
            }}
          />
        ),
        level: 0,
        children: [
          {
            label: t("Νέα προδέσμευση"),
            level: 1,
            link: "/namePurchase/newPurchase",
          },
          {
            label: t("Οι προδεσμεύσεις μου"),
            level: 1,
            link: "/namePurchase/activePurchases",
          },
          {
            label: t("Συχνές Ερωτήσεις"),
            level: 1,
            link: "/namePurchase/instructions",
          },
        ],
      },
      ...(import.meta.env.VITE_COMPLAINTS_ACTIVATED === "true"
        ? [
            {
              label: t("Καταγγελίες κατά Υπόχρεων Γ.Ε.ΜΗ."),
              image: (
                <OutlinedFlagIcon
                  sx={{
                    width: "1.5rem",
                    height: "1.5rem",
                    color: "#003476",
                  }}
                />
              ),
              level: 0,
              children: [
                {
                  label: t("Νέα Καταγγελία"),
                  level: 1,
                  link: "/complaint/form",
                },
                {
                  label: t("Ιστορικό Καταγγελιών"),
                  level: 1,
                  link: "/complaint/history",
                },
              ],
            },
          ]
        : []),
      ...(import.meta.env.VITE_VIOLATION_CASES_ACTIVATED === "true"
        ? [
            {
              label: t("Παραβάσεις - Πρόστιμα"),
              image: (
                <GavelOutlinedIcon
                  sx={{
                    width: "1.5rem",
                    height: "1.5rem",
                    color: "#003476",
                  }}
                />
              ),
              level: 0,
              children: [
                {
                  label: t("Διαχείριση Παραβάσεων - Προστίμων"),
                  level: 1,
                  link: "/violation/company/casesDashboard",
                },
              ],
            },
          ]
        : []),
      {
        label: t("Εγχειρίδια Χρήσης"),
        image: (
          <MenuBookIcon
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              color: "#003476",
            }}
          />
        ),
        level: 0,
        children: [
          ...(import.meta.env.VITE_COMPLAINTS_ACTIVATED === "true"
            ? [
                {
                  label: t("Εγχειρίδιο χρήσης υποβολής καταγγελιών"),
                  level: 1,
                  link: "/manuals/complaintSubmissionManual.pdf",
                  isExternal: true,
                },
              ]
            : []),
          ...(import.meta.env.VITE_VIOLATION_CASES_ACTIVATED === "true"
            ? [
                {
                  label: t("Εγχειρίδιο χρήσης υπόθεσης Παράβασης-Προστίμου"),
                  level: 1,
                  link: "/manuals/violationCaseManualForObligor.pdf",
                  isExternal: true,
                },
              ]
            : []),
          {
            label: t("Εγχειρίδιο χρήσης προσωποποιημένων υπηρεσιών Γ.Ε.ΜΗ."),
            level: 1,
            link: "/manuals/manualBusiness.pdf",
            isExternal: true,
          },
        ],
      },
    ];
  } else if (isNonGemiUser) {
    return [
      {
        label: t("Αιτήσεις"),
        image: (
          <DescriptionOutlinedIcon
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              color: "#003476",
            }}
          />
        ),
        level: 0,
        children: [
          {
            label: t("Νέα Αίτηση για Πιστοποιητικά / Αντίγραφα"),
            level: 1,
            link: "/certificate/newCertificateApplicationForm",
          },
          {
            label: t("Αιτήσεις υπό Διεκπεραίωση"),
            level: 1,
            link: "/certificate/activeCertificateApplications",
          },
          {
            label: t("Διεκπεραιωμένες Αιτήσεις"),
            level: 1,
            link: "/certificate/pastCertificateApplications",
          },
        ],
      },
      {
        label: t("Πληρωμές"),
        image: (
          <AccountBalanceOutlinedIcon
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              color: "#003476",
            }}
          />
        ),
        level: 0,
        children: [
          {
            label: t("Ιστορικό Πληρωμών"),
            level: 1,
            link: "/payment/listCertificatePayments",
          },
        ],
      },
      {
        label: t("Ενημέρωση για μεταβολές επιχείρησης"),
        image: (
          <InfoOutlinedIcon
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              color: "#003476",
            }}
          />
        ),
        level: 0,
        children: [
          {
            label: t("Θέλω να ακολουθήσω νέα επιχείρηση"),
            level: 1,
            link: "/followCompany/searchCompanyToFollow",
          },
          {
            label: t("Λίστα επιχειρήσεων που ακολουθώ"),
            level: 1,
            link: "/followCompany/myFollow",
          },
        ],
      },
      {
        label: t("Προδεσμεύσεις"),
        image: (
          <ChecklistRtlOutlinedIcon
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              color: "#003476",
            }}
          />
        ),
        level: 0,
        children: [
          {
            label: t("Νέα προδέσμευση"),
            level: 1,
            link: "/namePurchase/newPurchase",
          },
          {
            label: t("Οι προδεσμεύσεις μου"),
            level: 1,
            link: "/namePurchase/activePurchases",
          },
          {
            label: t("Χρήση Προδέσμευσης Επωνυμίας / ΔΤ για Μη Υπόχρεους ΓΕΜΗ"),
            level: 1,
            link: "/namePurchase/usePurchase",
          },
        ],
      },
      {
        label: t("Απευθείας Δεσμεύσεις"),
        image: (
          <FolderOpenOutlinedIcon
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              color: "#003476",
            }}
          />
        ),
        level: 0,
        children: [
          {
            label: t("Απευθείας Δέσμευση Επωνυμίας / Διακριτικού Τίτλου"),
            level: 1,
            link: "/namePurchase/directPurchase",
          },
          {
            label: t(
              "Εκκρεμείς Υποθέσεις Απευθείας Δέσμευσης Επωνυμίας / Διακριτικού Τίτλου",
            ),
            level: 1,
            link: "/namePurchase/pendingDirectPurchases",
          },
        ],
      },
      {
        label: t("Ιστορικό Επωνυμιών / ΔΤ μη Υπόχρεων ΓΕΜΗ"),
        image: (
          <WorkHistoryOutlinedIcon
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              color: "#003476",
            }}
          />
        ),
        level: 0,
        children: [
          {
            label: t("Ιστορικό Δηλώσεων Επωνυμιών / ΔΤ για μη Υπόχρεους ΓΕΜΗ"),
            level: 1,
            link: "/namePurchase/companyNames",
          },
        ],
      },
      ...(import.meta.env.VITE_COMPLAINTS_ACTIVATED === "true"
        ? [
            {
              label: t("Καταγγελίες κατά Υπόχρεων Γ.Ε.ΜΗ."),
              image: (
                <OutlinedFlagIcon
                  sx={{
                    width: "1.5rem",
                    height: "1.5rem",
                    color: "#03476",
                  }}
                />
              ),
              level: 0,
              children: [
                {
                  label: t("Νέα Καταγγελία"),
                  level: 1,
                  link: "/complaint/formAuth",
                },
                {
                  label: t("Ιστορικό Καταγγελιών"),
                  level: 1,
                  link: "/complaint/history",
                },
              ],
            },
          ]
        : []),
      ...(import.meta.env.VITE_COMPLAINTS_ACTIVATED === "true"
        ? [
            {
              label: t("Εγχειρίδια Χρήσης"),
              image: (
                <MenuBookIcon
                  sx={{
                    width: "1.5rem",
                    height: "1.5rem",
                    color: "#003476",
                  }}
                />
              ),
              level: 0,
              children: [
                {
                  label: t("Εγχειρίδιο χρήσης υποβολής καταγγελιών"),
                  level: 1,
                  link: "/manuals/complaintSubmissionManual.pdf",
                  isExternal: true,
                },
              ],
            },
          ]
        : []),
    ];
  } else if (
    isSupervisorAssignee &&
    !isChamber &&
    !isGGEAdmin &&
    !isSystemAdmin
  ) {
    return [
      {
        label: t("Εποπτεία Υπηρεσιών Γ.Ε.ΜΗ."),
        image: (
          <ApartmentOutlinedIcon
            sx={{ width: "1.5rem", height: "1.5rem", color: "#003476" }}
          />
        ),
        level: 0,
        children: [
          {
            label: t("Υποθέσεις παραβάσεων - προστίμων (για εποπτεία)"),
            level: 1,
            link: "/violation/supervisoryTeamViolationApplications",
          },
          {
            label: t("Υποθέσεις καταγγελιών (για εποπτεία)"),
            level: 1,
            link: "/complaint/supervisoryTeamComplaintApplications",
          },
          {
            label: t("Υποθέσεις δειγματοληπτικών ελέγχων (για εποπτεία)"),
            level: 1,
            link: "/sampleInspection/sampleInspectionSupervisoryDashboard",
          },
          {
            label: t("Ιστορικό αναθέσεων εποπτείας"),
            level: 1,
            link: "/supervisoryTeam/assigneeHistory",
          },
        ],
      },
      {
        label: t("Εγχειρίδια Χρήσης"),
        image: (
          <MenuBookIcon
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              color: "#003476",
            }}
          />
        ),
        level: 0,
        children: [
          {
            label: t("Εγχειρίδιο Ομάδας Εποπτείας"),
            level: 1,
            link: "/manuals/supervisorAssigneeManual.pdf",
            isExternal: true,
          },
        ],
      },
    ];
  } else if (
    isSupervisorAssigner &&
    !isChamber &&
    !isGGEAdmin &&
    !isSystemAdmin
  ) {
    return [
      {
        label: t("Εποπτεία Υπηρεσιών Γ.Ε.ΜΗ."),
        image: (
          <ApartmentOutlinedIcon
            sx={{ width: "1.5rem", height: "1.5rem", color: "#003476" }}
          />
        ),
        level: 0,
        children: [
          {
            label: t("Νέα Ανάθεση Υ.Γ.Ε.ΜΗ."),
            level: 1,
            link: "/supervisoryTeam/createAssignment",
          },
          {
            label: t("Διαχείριση Αναθέσεων"),
            level: 1,
            link: "/supervisoryTeam/assignerHistory",
          },
        ],
      },
    ];
  } else if (isChamber) {
    return [
      {
        label: t("Αιτήσεις Μεταβολής"),
        image: (
          <ArticleOutlinedIcon
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              color: "#003476",
            }}
          />
        ),
        level: 0,
        children: [
          {
            label: t("Νέα Αίτηση Μεταβολής"),
            level: 1,
            link: "/chamber/newApplication",
          },
          {
            label: t("Αιτήσεις που απαιτούν δική μου ενέργεια"),
            level: 1,
            link: "/chamber/activeOwnOnlyApplications",
          },
          {
            label: t(
              "Αιτήσεις σε εξέλιξη (δικές μου ή γενικά στην Υ. Γ.Ε.ΜΗ.)",
            ),
            level: 1,
            link: "/chamber/activeOwnApplications",
          },
          {
            label: t(
              "Αιτήσεις που δεν έχουν ανατεθεί ακόμα - Χωρίς εκκρεμότητα κάποιας πληρωμής",
            ),
            level: 1,
            link: "/chamber/activeChamberApplicationsPaid",
          },
          {
            label: t("Αιτήσεις με οποιαδήποτε εκκρεμότητα πληρωμής"),
            level: 1,
            link: "/chamber/activeChamberApplicationsUnPaid",
          },
          {
            label: t("Αρχείο εκπληρωμένων Υποθέσεων"),
            level: 1,
            link: "/chamber/pastOwnApplications",
          },
          ...(isOrgAdmin
            ? [
                {
                  label: t("Ανατεθειμένες σε ανενεργό χρήστη (Μεταβολές)"),
                  level: 1,
                  link: "/chamber/inactiveApplications",
                },
              ]
            : []),
        ],
      },
      {
        label: t("Πιστοποιητικά/Αντίγραφα"),
        image: (
          <DescriptionOutlinedIcon
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              color: "#003476",
            }}
          />
        ),
        level: 0,
        children: [
          {
            label: isOrgAdmin
              ? t(
                  "Αιτήσεις για Πιστοποιητικά/Αντίγραφα σε εξέλιξη (δικές μου ή γενικά στην Υ. Γ.Ε.ΜΗ.)",
                )
              : t(
                  "Αιτήσεις για Πιστοποιητικά/Αντίγραφα σε εξέλιξη (δικές μου)",
                ),
            level: 1,
            link: "/chamber/activeOwnCertificateApplications",
          },
          {
            label: t(
              "Αιτήσεις για Πιστοποιητικά/Αντίγραφα που δεν έχουν ανατεθεί ακόμη - Χωρίς εκκρεμότητα κάποιας πληρωμής",
            ),
            level: 1,
            link: "/chamber/activeChamberCertificateApplicationsPaid",
          },
          {
            label: t(
              "Αιτήσεις για Πιστοποιητικά/Αντίγραφα που δεν έχουν ανατεθεί ακόμη - Με οποιαδήποτε εκκρεμότητα πληρωμής",
            ),
            level: 1,
            link: "/chamber/activeChamberCertificateApplicationsUnpaid",
          },
          {
            label: t(
              "Αρχείο Εκπληρωμένων Υποθέσεων για Πιστοποιητικά/Αντίγραφα",
            ),
            level: 1,
            link: "/chamber/pastOwnCertificateApplications",
          },
          ...(isOrgAdmin
            ? [
                {
                  label: t("Ανατεθειμένες σε ανενεργό χρήστη (Πιστοποιητικά)"),
                  level: 1,
                  link: "/chamber/inactiveCertificateApplications",
                },
              ]
            : []),
        ],
      },
      ...(import.meta.env.VITE_ClEANSING_ACTIVATED === "true"
        ? [
            {
              label: t("Υποθέσεις Εκκαθάρισης Δεδομένων"),
              image: (
                <RuleFolderOutlinedIcon
                  sx={{
                    width: "1.5rem",
                    height: "1.5rem",
                    color: "#003476",
                  }}
                />
              ),
              level: 0,
              children: [
                {
                  label: isOrgAdmin
                    ? t(
                        "Υποθέσεις σε εξέλιξη (δικές μου ή γενικά στην Υ. Γ.Ε.ΜΗ.)",
                      )
                    : t("Υποθέσεις σε εξέλιξη"),
                  level: 1,
                  link: "/chamber/activeAadeCases",
                },
                ...(isOrgAdmin
                  ? [
                      {
                        label: t("Υποθέσεις που δεν έχουν ανατεθεί ακόμα"),
                        level: 1,
                        link: "/chamber/unRegisteredAadeCases",
                      },
                    ]
                  : []),
                {
                  label: isOrgAdmin
                    ? t(
                        "Εκπληρωμένες Υποθέσεις (δικές μου ή γενικά στην Υ. Γ.Ε.ΜΗ.)",
                      )
                    : t("Εκπληρωμένες Υποθέσεις"),
                  level: 1,
                  link: "/chamber/pastAadeCases",
                },
              ],
            },
          ]
        : []),
      {
        label: t("Έλεγχοι Διαθεσιμότητας Προδέσμευσης Επωνυμίας / ΔΤ"),
        image: (
          <ChecklistRtlOutlinedIcon
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              color: "#003476",
            }}
          />
        ),
        level: 0,
        children: [
          {
            label: t(
              "Ενεργοί Έλεγχοι Διαθεσιμότητας Προδέσμευσης Επωνυμίας / ΔΤ",
            ),
            level: 1,
            link: "/namePurchase/namePurchaseClaimsForChamber",
          },
          {
            label: t(
              "Διεκπεραιωμένοι Έλεγχοι Διαθεσιμότητας Προδέσμευσης Επωνυμίας / ΔΤ",
            ),
            level: 1,
            link: "/namePurchase/namePurchasePastClaimsForChamber",
          },
        ],
      },
      {
        label: t("Ταμείο"),
        image: (
          <AccountBalanceOutlinedIcon
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              color: "#003476",
            }}
          />
        ),
        level: 0,
        children: [
          ...(isOrgAdmin
            ? [
                {
                  label: t("Προβλεπόμενα Έσοδα από ΓΕΜΗ"),
                  level: 1,
                  link: "/chamber/cashierForecast",
                },
                {
                  label: t("Εμβάσματα από ΚΥ ΓΕΜΗ"),
                  level: 1,
                  link: "/chamber/cashierRemittances",
                },
              ]
            : []),
          {
            label: t("Αναζήτηση πληρωμών επιχείρησης"),
            level: 1,
            link: "/chamber/findCompanyPayments",
          },
          {
            label: t("Ενημέρωση για έκπτωση συνδρομής επιχείρησης"),
            level: 1,
            link: "/chamber/companyReducedSubscription",
          },
          ...(isOrgAdmin
            ? [
                {
                  label: t("Στατιστικά"),
                  level: 1,
                  link: "/chamber/cashierStats",
                },
              ]
            : []),
        ],
      },
      ...(isOrgAdmin
        ? [
            {
              label: t("Καταστάσεις"),
              image: (
                <PictureAsPdfOutlinedIcon
                  sx={{
                    width: "1.5rem",
                    height: "1.5rem",
                    color: "#003476",
                  }}
                />
              ),
              level: 0,
              children: [
                {
                  label: t("Οικονομικές Καταστάσεις"),
                  level: 1,
                  link: "/chamber/financialReport",
                },
                {
                  label: t("Ετήσιες Οικονομικές Καταστάσεις"),
                  level: 1,
                  link: "/chamber/cashierReport",
                },
                {
                  label: t("Οφειλές Ετήσιων Τελών Τήρησης Μερίδας"),
                  level: 1,
                  link: "/chamber/unpaidSubscriptions",
                },
                {
                  label: t("Οφειλές Βεβαιωμένων Ετήσιων Τελών"),
                  level: 1,
                  link: "/chamber/kedeSubscriptions",
                },
              ],
            },
          ]
        : []),
      {
        label: t("Διαχείριση"),
        image: (
          <ManageAccountsOutlinedIcon
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              color: "#003476",
            }}
          />
        ),
        level: 0,
        children: [
          {
            label: t("Στοιχεία Επικοινωνίας Εταιρείας"),
            level: 1,
            link: "/chamber/userDetails",
          },
          {
            label: t("Στοιχεία πληρωμής ετήσιου τέλους"),
            level: 1,
            link: "/chamber/companySubscription",
          },
          ...(isOrgAdmin
            ? [
                {
                  label: t("Διαγραφή ΚΑΚ από αίτηση"),
                  level: 1,
                  link: "/chamber/deleteKak",
                },
                {
                  label: t("Λίστα με πλήθος ενεργών επιχειρήσεων"),
                  level: 1,
                  link: "/chamber/activeBusinessCount",
                },
              ]
            : []),
          {
            label: t("Αιτήματα Προσθήκης Νόμιμων Εκπροσώπων"),
            level: 1,
            link: "/beneficiary/representativesRequestsForChamber",
          },
          {
            label: t("Οδηγίες Χρήσης"),
            isExternal: true,
            level: 1,
            link: "/manuals/manualChamber.pdf",
          },
          ...(import.meta.env.VITE_IMPERSONATION_ACTIVATED === "true"
            ? [
                {
                  label: t("Σύνδεση ως Επιχείρηση"),
                  level: 1,
                  link: "/chamber/impersonationPageBusiness",
                },
              ]
            : []),
        ],
      },
      ...(import.meta.env.VITE_COMPLAINTS_ACTIVATED === "true"
        ? [
            {
              label: t("Καταγγελίες"),
              image: (
                <OutlinedFlagIcon
                  sx={{
                    width: "1.5rem",
                    height: "1.5rem",
                    color: "#003476",
                  }}
                />
              ),
              level: 0,
              children: [
                ...(isSampleInitiator
                  ? [
                      {
                        label: t("Καταγγελίες (όλων των Υ.Γ.Ε.ΜΗ.)"),
                        level: 1,
                        link: "/complaint/adminComplaintApplications",
                      },
                    ]
                  : []),
                {
                  label: t("Υποθέσεις Καταγγελιών σε εξέλιξη"),
                  level: 1,
                  link: "/complaint/activeComplaintApplications",
                },
                ...(isOrgAdmin
                  ? [
                      {
                        label: t("Μη ανατεθειμένες Υποθέσεις Καταγγελιών"),
                        level: 1,
                        link: "/complaint/pendingComplaintApplications",
                      },
                    ]
                  : []),
                {
                  label: t("Ολοκληρωμένες Υποθέσεις Καταγγελιών"),
                  level: 1,
                  link: "/complaint/pastComplaintApplications",
                },
              ],
            },
          ]
        : []),
      ...(import.meta.env.VITE_SAMPLE_INSPECTION_ACTIVATED === "true"
        ? [
            {
              label: t("Δειγματοληπτικοί Έλεγχοι"),
              image: (
                <FactCheckOutlinedIcon
                  sx={{
                    width: "1.5rem",
                    height: "1.5rem",
                    color: "#003476",
                  }}
                />
              ),
              level: 0,
              children: [
                ...(isSampleInitiator
                  ? [
                      {
                        label: t("Δημιουργία νέου δειγματοληπτικού ελέγχου"),
                        level: 1,
                        link: "/sampleInspection/sample/create",
                      },
                      {
                        label: t(
                          "Υποθέσεις δειγματοληπτικών ελέγχων (όλων των Υ.Γ.Ε.ΜΗ.)",
                        ),
                        level: 1,
                        link: "/sampleInspection/sample/initiatorsDashboard",
                      },
                    ]
                  : []),
                ...(isOrgAdmin
                  ? [
                      {
                        label: t(
                          "Υποθέσεις δειγματοληπτικών ελέγχων της Υ.Γ.Ε.ΜΗ.",
                        ),
                        level: 1,
                        link: "/sampleInspection/sample/chamberDashboard",
                      },
                    ]
                  : []),
                {
                  label: t("Έλεγχος επιχειρήσεων που μου έχουν ανατεθεί"),
                  level: 1,
                  link: "/sampleInspection/case/dashboard",
                },
              ],
            },
          ]
        : []),
      ...(import.meta.env.VITE_VIOLATION_CASES_ACTIVATED === "true"
        ? [
            {
              label: t("Παραβάσεις - Πρόστιμα"),
              image: (
                <GavelOutlinedIcon
                  sx={{
                    width: "1.5rem",
                    height: "1.5rem",
                    color: "#003476",
                  }}
                />
              ),
              level: 0,
              children: [
                ...(isSampleInitiator
                  ? [
                      {
                        label: t(
                          "Υποθέσεις παραβάσεων - προστίμων (Όλων των Υ.Γ.Ε.ΜΗ.)",
                        ),
                        level: 1,
                        link: "/violation/admin/casesDashboard",
                      },
                    ]
                  : []),
                ...(isOrgAdmin
                  ? [
                      {
                        label: t(
                          "Υποθέσεις παραβάσεων - προστίμων σε εξέλιξη (δικές μου ή γενικά στην Υ.Γ.Ε.ΜΗ.",
                        ),
                        level: 1,
                        link: "/violation/chamber/casesDashboard",
                      },
                      ...(!chamberHasOneAdmin
                        ? [
                            {
                              label: t(
                                "Υποθέσεις που απαιτούν ενέργεια προϊσταμένου (χωρίς υπεύθυνο προϊστάμενο)",
                              ),
                              level: 1,
                              link: "/violation/chamber/assignChamberAdminDashboard",
                            },
                          ]
                        : []),
                      {
                        label: t(
                          "Μη ανατεθειμένες υποθέσεις (χωρίς υπεύθυνο διεκπεραίωσης)",
                        ),
                        level: 1,
                        link: "/violation/chamber/assignChamberUserDashboard",
                      },
                      {
                        label: t("Ολοκληρωμένες υποθέσεις παράβασης προστίμου"),
                        level: 1,
                        link: "/violation/chamber/pastViolationCases",
                      },
                    ]
                  : []),
                {
                  label: t("Υπολογισμός ύψους προστίμου"),
                  level: 1,
                  link: "/violation/fine/calculation",
                },
                {
                  label: t(
                    "Έναρξη διαδικασίας καταγραφής παράβασης - επιβολής προστίμου",
                  ),
                  level: 1,
                  link: "/violation/newCase",
                },
                ...(isChamberOnly
                  ? [
                      {
                        label: t("Υποθέσεις σε εξέλιξη (δικές μου)"),
                        level: 1,
                        link: "/violation/chamber/myChamberUserCasesDashboard",
                      },
                      {
                        label: t("Υποθέσεις oλοκληρωμένες (δικές μου)"),
                        level: 1,
                        link: "/violation/chamber/myChamberUserCompletedCasesDashboard",
                      },
                    ]
                  : []),
              ],
            },
          ]
        : []),
      ...(isSupervisorAssigner
        ? [
            {
              label: t("Εποπτεία Υπηρεσιών Γ.Ε.ΜΗ."),
              image: (
                <ApartmentOutlinedIcon
                  sx={{ width: "1.5rem", height: "1.5rem", color: "#003476" }}
                />
              ),
              level: 0,
              children: [
                {
                  label: t("Νέα Ανάθεση Υ.Γ.Ε.ΜΗ."),
                  level: 1,
                  link: "/supervisoryTeam/createAssignment",
                },
                {
                  label: t("Διαχείριση Αναθέσεων"),
                  level: 1,
                  link: "/supervisoryTeam/assignerHistory",
                },
              ],
            },
          ]
        : []),
      ...(isSupervisorAssignee
        ? [
            {
              label: t("Εποπτεία Υπηρεσιών Γ.Ε.ΜΗ."),
              image: (
                <ApartmentOutlinedIcon
                  sx={{ width: "1.5rem", height: "1.5rem", color: "#003476" }}
                />
              ),
              level: 0,
              children: [
                {
                  label: t("Υποθέσεις παραβάσεων - προστίμων (για εποπτεία)"),
                  level: 1,
                  link: "/violation/supervisoryTeamViolationApplications",
                },
                {
                  label: t("Υποθέσεις καταγγελιών (για εποπτεία)"),
                  level: 1,
                  link: "/complaint/supervisoryTeamComplaintApplications",
                },
                {
                  label: t("Υποθέσεις δειγματοληπτικών ελέγχων (για εποπτεία)"),
                  level: 1,
                  link: "/sampleInspection/sampleInspectionSupervisoryDashboard",
                },
                {
                  label: t("Ιστορικό αναθέσεων εποπτείας"),
                  level: 1,
                  link: "/supervisoryTeam/assigneeHistory",
                },
              ],
            },
          ]
        : []),
      ...(import.meta.env.VITE_SAMPLE_INSPECTION_ACTIVATED === "true" ||
      import.meta.env.VITE_COMPLAINTS_ACTIVATED === "true" ||
      import.meta.env.VITE_VIOLATION_CASES_ACTIVATED === "true"
        ? [
            {
              label: t("Εγχειρίδια Χρήσης"),
              image: (
                <MenuBookIcon
                  sx={{
                    width: "1.5rem",
                    height: "1.5rem",
                    color: "#003476",
                  }}
                />
              ),
              level: 0,
              children: [
                ...(import.meta.env.VITE_SAMPLE_INSPECTION_ACTIVATED === "true"
                  ? [
                      ...(import.meta.env.VITE_SAMPLE_INSPECTION_ACTIVATED ===
                      "true"
                        ? [
                            ...(isSampleInitiator
                              ? [
                                  {
                                    label: t(
                                      "Εγχειρίδιο χρήσης δειγματοληπτικού ελέγχου (για εξουσιοδοτημένους χρήστες)",
                                    ),
                                    level: 1,
                                    link: "/manuals/samplingInspectionInitiatorManual.pdf",
                                    isExternal: true,
                                  },
                                ]
                              : []),
                            {
                              label: t(
                                "Εγχειρίδιο χρήσης δειγματοληπτικού ελέγχου (για Υ.Γ.Ε.ΜΗ.)",
                              ),
                              level: 1,
                              link: "/manuals/samplingInspectionManual.pdf",
                              isExternal: true,
                            },
                          ]
                        : []),
                    ]
                  : []),
                ...(import.meta.env.VITE_COMPLAINTS_ACTIVATED === "true"
                  ? [
                      {
                        label: t("Εγχειρίδιο χρήσης διαχείρισης καταγγελιών"),
                        level: 1,
                        link: "/manuals/complaintsManagementManual.pdf",
                        isExternal: true,
                      },
                    ]
                  : []),
                ...(import.meta.env.VITE_VIOLATION_CASES_ACTIVATED === "true"
                  ? [
                      {
                        label: t(
                          "Εγχειρίδιο χρήσης καταγραφής και διαχείρισης υπόθεσης παράβασης - προστίμου",
                        ),
                        level: 1,
                        link: "/manuals/violationCaseManagementManual.pdf",
                        isExternal: true,
                      },
                    ]
                  : []),
                ...(isSupervisorAssignee
                  ? [
                      {
                        label: t("Εγχειρίδιο Ομάδας Εποπτείας"),
                        level: 1,
                        link: "/manuals/supervisorAssigneeManual.pdf",
                        isExternal: true,
                      },
                    ]
                  : []),
              ],
            },
          ]
        : []),
    ];
  } else if (isCashier) {
    return [
      {
        label: t("Εμβάσματα"),
        image: (
          <AccountBalanceOutlinedIcon
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              color: "#003476",
            }}
          />
        ),
        level: 0,
        children: [
          {
            label: t("Νέο Έμβασμα"),
            level: 1,
            link: "/cashier/newRemittance",
          },
          {
            label: t("Αρχεία Εμβασμάτων"),
            level: 1,
            link: "/cashier/remittancesFiles",
          },
          {
            label: t("IBAN Υ. Γ.Ε.ΜΗ."),
            level: 1,
            link: "/cashier/chamberDetails",
          },
        ],
      },
      {
        label: t("Πληρωμές"),
        image: (
          <ReceiptOutlinedIcon
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              color: "#003476",
            }}
          />
        ),
        level: 0,
        children: [
          {
            label: t("Προβλεπόμενα έσοδα από ΓΕΜΗ"),
            level: 1,
            link: "/cashier/paymentSums",
          },
          {
            label: t("Αρχεία επιβεβαίωσης από την ΔΙΑΣ"),
            level: 1,
            link: "/cashier/diasFiles",
          },
        ],
      },
      {
        label: t("Καταστάσεις"),
        image: (
          <PictureAsPdfOutlinedIcon
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              color: "#003476",
            }}
          />
        ),
        level: 0,
        children: [
          {
            label: t("Τριμηνιαίες Καταστάσεις ανά Υ. Γ.Ε.ΜΗ."),
            level: 1,
            link: "/cashier/threeMonthReport",
          },
          //προς το παρόν μη διαθέσιμο
          // {
          //   label: t("Μη Πληρωμή Ετήσιων Τελών Τήρησης Μερίδας"),
          //   level: 1,
          //   link: "/cashier/unpaidSubscriptions",
          // },
        ],
      },
    ];
  } else if (isRegion) {
    return [
      {
        label: t("Αιτήσεις"),
        image: (
          <ArticleOutlinedIcon
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              color: "#003476",
            }}
          />
        ),
        level: 0,
        children: [
          {
            label: isOrgAdmin
              ? t("Αιτήσεις σε εξέλιξη (δικές μου ή γενικά στην ΠΕ)")
              : t("Αιτήσεις σε εξέλιξη (δικές μου)"),
            level: 1,
            link: "/region/activeOwnApplications",
          },
          ...(isOrgAdmin
            ? [
                {
                  label: t("Αιτήσεις που δεν έχουν ανατεθεί ακόμη"),
                  level: 1,
                  link: "/region/activeRegionApplications",
                },
              ]
            : []),
          {
            label: t("Αρχείο Εκπληρωμένων Υποθέσεων"),
            level: 1,
            link: "/region/pastOwnApplications",
          },
        ],
      },
    ];
  } else if (isSystemAdmin || isGGEAdmin) {
    return [
      {
        label: t("Αρχείο Αιτήσεων"),
        image: (
          <ArticleOutlinedIcon
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              color: "#003476",
            }}
          />
        ),
        level: 0,
        children: [
          {
            label: t("Μεταβολές"),
            level: 1,
            link: "/admin/applications",
          },
          {
            label: t("Πιστοποιητικά / Αντίγραφα"),
            level: 1,
            link: "/admin/certificates",
          },
          {
            label: t("Προδεσμεύσεις Επωνυμιών / ΔΤ"),
            level: 1,
            link: "/admin/nameTitlePurchase",
          },
          {
            label: t(
              "Έλεγχος Διαθεσιμότητας Επωνυμίας / ΔΤ που έχουν προωθηθεί προς ΤΥ ΓΕΜΗ",
            ),
            level: 1,
            link: "/admin/nameTitleClaimPurchase",
          },
          {
            label: t("Ανατεθειμένες σε διεγραμμένο χρήστη (Μεταβολές)"),
            level: 1,
            link: "/admin/inactiveApplications",
          },
          {
            label: t("Ανατεθειμένες σε διεγραμμένο χρήστη (Πιστοποιητικά)"),
            level: 1,
            link: "/admin/inactiveCertificateApplications",
          },
        ],
      },
      {
        label: t("Ταμείο"),
        image: (
          <AccountBalanceOutlinedIcon
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              color: "#003476",
            }}
          />
        ),
        level: 0,
        children: [
          {
            label: t("Αρχείο πληρωμών συνδρομών και μεταβολών"),
            level: 1,
            link: "/admin/payments",
          },
          {
            label: t("Αρχείο πληρωμών πιστοποιητικών"),
            level: 1,
            link: "/admin/certPayments",
          },
          ...(isSystemAdmin
            ? [
                {
                  label: t("Προβλεπόμενα έσοδα από ΓΕΜΗ"),
                  level: 1,
                  link: "/admin/paymentSums",
                },
              ]
            : []),
        ],
      },
      ...(import.meta.env.VITE_ClEANSING_ACTIVATED === "true"
        ? [
            {
              label: t("Υποθέσεις Εκκαθάρισης Δεδομένων"),
              image: (
                <RuleFolderOutlinedIcon
                  sx={{
                    width: "1.5rem",
                    height: "1.5rem",
                    color: "#003476",
                  }}
                />
              ),
              level: 0,
              children: [
                {
                  label: t(
                    "Υποθέσεις αναντιστοιχιών σε εξέλιξη από Υ. Γ.Ε.ΜΗ.",
                  ),

                  level: 1,
                  link: "/admin/activeAadeCases",
                },
                {
                  label: t("Υποθέσεις αναντιστοιχιών μη ανατεθημένες"),
                  level: 1,
                  link: "/admin/unRegisteredAadeCases",
                },

                {
                  label: t(
                    "Υποθέσεις αναντιστοιχιών ολοκληρωμένες από Υ. Γ.Ε.ΜΗ.",
                  ),
                  level: 1,
                  link: "/admin/pastAadeCases",
                },
              ],
            },
          ]
        : []),

      ...(isSystemAdmin
        ? [
            {
              label: t("Διατραπεζικές πληρωμές ΔΙΑΣ"),
              image: (
                <ReceiptOutlinedIcon
                  sx={{
                    width: "1.5rem",
                    height: "1.5rem",
                    color: "#003476",
                  }}
                />
              ),
              level: 0,
              children: [
                {
                  label: t("Αρχεία επιβεβαίωσης από την ΔΙΑΣ"),
                  level: 1,
                  link: "/admin/diasFiles",
                },
                {
                  label: t("ΔΙΑΣ online"),
                  level: 1,
                  link: "/admin/diasOnline",
                },
                {
                  label: t("Διόρθωση πληρωμής"),
                  level: 1,
                  link: "/admin/diasFix",
                },
                {
                  label: t(
                    "Διόρθωση πληρωμής λόγω πολλαπλής χρήσης κωδικού πληρωμής",
                  ),
                  level: 1,
                  link: "/admin/fixWrongRi3DiasPayment",
                },
                //add diasFaults if fixed
              ],
            },
          ]
        : []),
      {
        label: t("Καταστάσεις"),
        image: (
          <PictureAsPdfOutlinedIcon
            sx={{
              width: "1.5rem",
              height: "1.5rem",
              color: "#003476",
            }}
          />
        ),
        level: 0,
        children: [
          {
            label: t("Οικονομικές Καταστάσεις ανά Υ. Γ.Ε.ΜΗ."),
            level: 1,
            link: "/admin/financialReport",
          },
          ...(isSystemAdmin
            ? [
                {
                  label: t("Ετήσιες Καταστάσεις ανά Υ. Γ.Ε.ΜΗ."),
                  level: 1,
                  link: "/admin/cashierReport",
                },
                {
                  label: t("Πληρωμένες Συνδρομές"),
                  level: 1,
                  link: "/admin/paidSubscriptions",
                },
                {
                  label: t("Μη Πληρωμένες Συνδρομές"),
                  level: 1,
                  link: "/admin/unpaidSubscriptions",
                },
                {
                  label: t("Οφειλές Βεβαιωμένων Ετήσιων Τελών"),
                  level: 1,
                  link: "/admin/kedeSubscriptions",
                },
              ]
            : []),
        ],
      },

      ...(isSystemAdmin
        ? [
            {
              label: t("Επιστροφές Πληρωμών"),
              image: (
                <EventRepeatOutlinedIcon
                  sx={{
                    width: "1.5rem",
                    height: "1.5rem",
                    color: "#003476",
                  }}
                />
              ),
              level: 0,
              children: [
                {
                  label: t("Ενημέρωση για Επιστροφές Πληρωμών"),
                  level: 1,
                  link: "/admin/updatePaymentsForRefund",
                },
                {
                  label: t(
                    "Λίστα αιτήσεων μεταβολών με εκκρεμότητα επιστροφής χρημάτων",
                  ),
                  level: 1,
                  link: "/admin/viewPaymentsForRefund",
                },
              ],
            },
            {
              label: t("Διαχείριση"),
              image: (
                <ManageAccountsOutlinedIcon
                  sx={{
                    width: "1.5rem",
                    height: "1.5rem",
                    color: "#003476",
                  }}
                />
              ),
              level: 0,
              children: [
                {
                  label: t("e-Payment Inspector"),
                  level: 1,
                  link: "/admin/inspectPayment",
                },
                {
                  label: t("Background Job Runner"),
                  level: 1,
                  link: "/admin/listJobs",
                },
                //Προσωρινό κρύψιμο από το μενού
                // {
                //   label: t("Χειροκίνητος συγχρονισμός ΑΑΔΕ"),
                //   level: 1,
                //   link: "/admin/aadeManualSync",
                // },
                {
                  label: t("Τύποι Αιτήσεων"),
                  level: 1,
                  link: "/admin/appTypes",
                },
                {
                  label: t("Τύποι Πιστοποιητικών"),
                  level: 1,
                  link: "/admin/certTypes",
                },
                {
                  label: t("Διαχειριστές αιτήσεων"),
                  level: 1,
                  link: "/admin/orgAdmin",
                },
                {
                  label: t("Λίστα με ενεργές επιχειρήσεις"),
                  level: 1,
                  link: "/admin/activeBusiness",
                },
                {
                  label: t("Λίστα με πλήθος ενεργών επιχειρήσεων"),
                  level: 1,
                  link: "/admin/activeBusinessCount",
                },
                {
                  label: t("Δημιουργία αποδείξεων που δεν φτιάχτηκαν αυτόματα"),
                  level: 1,
                  link: "/admin/fixMissingInvoices",
                },
                {
                  label: t("IBAN Υ. Γ.Ε.ΜΗ."),
                  level: 1,
                  link: "/admin/chamberDetails",
                },
                {
                  label: t("Ρυθμίσεις κόστους συνδρομών ΓΕΜΗ"),
                  level: 1,
                  link: "/admin/showSubscr",
                },
                {
                  label: t("Στοιχεία πρόσβασης χρήστη"),
                  level: 1,
                  link: "/admin/alterUser",
                },
                {
                  label: t("Maintenance alert"),
                  level: 1,
                  link: "/admin/maintenanceForm",
                },
                {
                  label: t("Ενημέρωση για νέες λειτουργικότητες"),
                  level: 1,
                  link: "/admin/newChamberFeatureForm",
                },
                {
                  label: t("Σύνδεση ως χρήστης Υ ΓΕΜΗ"),
                  level: 1,
                  link: "/admin/impersonationPage",
                },
                {
                  label: t("Σύνδεση ως Επιχείρηση"),
                  level: 1,
                  link: "/admin/impersonationPageBusiness",
                },
                {
                  label: t("Σύνδεση ως μη υπόχρεος"),
                  level: 1,
                  link: "/admin/impersonationPageNonGemi",
                },
                {
                  label: t("Μαζική έναρξη καταγραφής παραβάσεων"),
                  level: 1,
                  link: "/admin/bulkViolationCases",
                },
              ],
            },
            ...(import.meta.env.VITE_FIREBASE_ACTIVE === "true"
              ? [
                  {
                    label: t("Σύστημα ειδοποιήσεων"),
                    image: (
                      <InfoOutlinedIcon
                        sx={{
                          width: "1.5rem",
                          height: "1.5rem",
                          color: "#003476",
                        }}
                      />
                    ),
                    level: 0,
                    children: [
                      {
                        label: t("Αποστολή ειδοποίησης προς Επιχειρήσεις"),
                        level: 1,
                        link: "/admin/sendNotificationToCompany",
                      },
                      {
                        label: t(
                          "Αποστολή ειδοποίησης προς όλους τους χρηστές",
                        ),
                        level: 1,
                        link: "/admin/sendNotificationToAllUsers",
                      },
                      {
                        label: t("Ιστορικό ειδοποιήσεων"),
                        level: 1,
                        link: "/admin/sendNotificationHistory",
                      },
                    ],
                  },
                ]
              : []),
            {
              label: t("Προβληματικές Υποθέσεις"),
              image: (
                <AssignmentLateOutlinedIcon
                  sx={{
                    width: "1.5rem",
                    height: "1.5rem",
                    color: "#003476",
                  }}
                />
              ),
              level: 0,
              children: [
                {
                  label: t("Προβληματικές Συνδρομές"),
                  level: 1,
                  link: "/admin/wrongSubscriptions",
                },
                {
                  label: t("Επιστροφή Ετήσιου Τέλους"),
                  level: 1,
                  link: "/admin/companyRefundSubscription",
                },
                {
                  label: t("Επανυπολογισμός Flags Αιτήσεων"),
                  level: 1,
                  link: "/admin/updateUnpaidSubscrFlags",
                },
                {
                  label: t("Ανάθεση Αίτησης σε Υ ΓΕΜΗ"),
                  level: 1,
                  link: "/admin/wrongChamberForApplication",
                },
                {
                  label: t("Διόρθωση Φόρμας Αίτησης"),
                  level: 1,
                  link: "/admin/fixApplicationForm",
                },
                {
                  label: t("Μεταφορά Αιτήσεων σε άλλη Υ ΓΕΜΗ"),
                  level: 1,
                  link: "/admin/moveAllAppToNewChamber",
                },
                {
                  label: t("Διαγραφή ΚΑΚ από αίτηση"),
                  level: 1,
                  link: "/admin/deleteKak",
                },
                {
                  label: t("Ενημέρωση για έκπτωση συνδρομής επιχείρησης"),
                  level: 1,
                  link: "/admin/companyReducedSubscription",
                },
                {
                  label: t("Εξαίρεση οφειλής συνδρομής"),
                  level: 1,
                  link: "/admin/subscriptionRelief",
                },
              ],
            },
            {
              label: t("Τελευταίες 100 κινήσεις"),
              image: (
                <WorkHistoryOutlinedIcon
                  sx={{
                    width: "1.5rem",
                    height: "1.5rem",
                    color: "#003476",
                  }}
                />
              ),
              level: 0,
              children: [
                {
                  label: t("Συνδρομών"),
                  level: 1,
                  link: "/admin/quickSubscriptions",
                },
                {
                  label: t("Πιστοποιητικών"),
                  level: 1,
                  link: "/admin/quickCertificates",
                },
                {
                  label: t("Εγγραφές Χρηστών Ν"),
                  level: 1,
                  link: "/admin/quickUserRegistrationsNonGemi",
                },
                {
                  label: t("Εγγραφές Χρηστών Μ"),
                  level: 1,
                  link: "/admin/quickUserRegistrationsGemi",
                },
              ],
            },
            {
              label: t("Διάθεση Ηλεκτρονικής Υπογραφής"),
              image: (
                <CreateOutlinedIcon
                  sx={{
                    width: "1.5rem",
                    height: "1.5rem",
                    color: "#003476",
                  }}
                />
              ),
              level: 0,
              children: [
                {
                  label: t("Ανάλυση Επιλογής Φ.Π."),
                  level: 1,
                  link: "/beneficiary/analyzeSelection",
                },
                {
                  label: t("Ανάλυση Αιτημάτων Προσθήκης Φ.Π."),
                  level: 1,
                  link: "/beneficiary/analyzeAddRequests",
                },
              ],
            },
          ]
        : []),
      ...(import.meta.env.VITE_COMPLAINTS_ACTIVATED === "true"
        ? [
            {
              label: t("Σύστημα Καταγγελιών"),
              image: (
                <OutlinedFlagIcon
                  sx={{
                    width: "1.5rem",
                    height: "1.5rem",
                    color: "#003476",
                  }}
                />
              ),
              level: 0,
              children: [
                {
                  label: t("Καταγγελίες (όλων των Υ.Γ.Ε.ΜΗ.)"),
                  level: 1,
                  link: "/complaint/adminComplaintApplications",
                },
              ],
            },
          ]
        : []),
      ...(import.meta.env.VITE_SAMPLE_INSPECTION_ACTIVATED === "true"
        ? [
            {
              label: t("Δειγματοληπτικοί Έλεγχοι"),
              image: (
                <FactCheckOutlinedIcon
                  sx={{
                    width: "1.5rem",
                    height: "1.5rem",
                    color: "#003476",
                  }}
                />
              ),
              level: 0,
              children: [
                ...(isSampleInitiator
                  ? [
                      {
                        label: t("Δημιουργία νέου δειγματοληπτικού ελέγχου"),
                        level: 1,
                        link: "/sampleInspection/samplecreate",
                      },
                    ]
                  : []),
                {
                  label: t(
                    "Υποθέσεις δειγματοληπτικών ελέγχων (όλων των Υ.Γ.Ε.ΜΗ.)",
                  ),
                  level: 1,
                  link: "/sampleInspection/sample/initiatorsDashboard",
                },
              ],
            },
            ...(import.meta.env.VITE_VIOLATION_CASES_ACTIVATED === "true"
              ? [
                  {
                    label: t("Παραβάσεις - Πρόστιμα"),
                    image: (
                      <GavelOutlinedIcon
                        sx={{
                          width: "1.5rem",
                          height: "1.5rem",
                          color: "#003476",
                        }}
                      />
                    ),
                    level: 0,
                    children: [
                      {
                        label: t(
                          "Υποθέσεις παραβάσεων - προστίμων (όλων των Υ.Γ.Ε.ΜΗ.)",
                        ),
                        level: 1,
                        link: "/violation/admin/casesDashboard",
                      },
                    ],
                  },
                ]
              : []),
          ]
        : []),
      ...(isSupervisorAssigner
        ? [
            {
              label: t("Εποπτεία Υπηρεσιών Γ.Ε.ΜΗ."),
              image: (
                <ApartmentOutlinedIcon
                  sx={{ width: "1.5rem", height: "1.5rem", color: "#003476" }}
                />
              ),
              level: 0,
              children: [
                {
                  label: t("Νέα Ανάθεση Υ.Γ.Ε.ΜΗ."),
                  level: 1,
                  link: "/supervisoryTeam/createAssignment",
                },
                {
                  label: t("Διαχείριση Αναθέσεων"),
                  level: 1,
                  link: "/supervisoryTeam/assignerHistory",
                },
              ],
            },
          ]
        : []),
      ...(isSupervisorAssignee
        ? [
            {
              label: t("Εποπτεία Υπηρεσιών Γ.Ε.ΜΗ."),
              image: (
                <ApartmentOutlinedIcon
                  sx={{ width: "1.5rem", height: "1.5rem", color: "#003476" }}
                />
              ),
              level: 0,
              children: [
                {
                  label: t("Υποθέσεις παραβάσεων - προστίμων (για εποπτεία)"),
                  level: 1,
                  link: "/violation/supervisoryTeamViolationApplications",
                },
                {
                  label: t("Υποθέσεις καταγγελιών (για εποπτεία)"),
                  level: 1,
                  link: "/complaint/supervisoryTeamComplaintApplications",
                },
                {
                  label: t("Υποθέσεις δειγματοληπτικών ελέγχων (για εποπτεία)"),
                  level: 1,
                  link: "/sampleInspection/sampleInspectionSupervisoryDashboard",
                },
                {
                  label: t("Ιστορικό αναθέσεων εποπτείας"),
                  level: 1,
                  link: "/supervisoryTeam/assigneeHistory",
                },
              ],
            },
          ]
        : []),
    ];
  }
  return [];
};
